import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Custom element that always renders "CART: N" text and updates live when the
 * cart is mutated. Unlike the default cart bubble it never hides at zero —
 * OVID's header shows the count unconditionally for logged-in customers.
 *
 * Usage:
 *   <cart-count-text data-template="Cart: __COUNT__">Cart: 0</cart-count-text>
 *
 * @extends {Component}
 */
class CartCountText extends Component {
  /** @type {string} */
  #template = 'Cart: __COUNT__';

  connectedCallback() {
    super.connectedCallback();

    const raw = this.getAttribute('data-template');
    if (raw) this.#template = raw;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * @param {CustomEvent<{ data?: { itemCount?: number, source?: string } }>} event
   */
  #onCartUpdate = (event) => {
    const nextCount = event.detail?.data?.itemCount;
    if (typeof nextCount !== 'number') return;

    const comingFromProductForm = event.detail?.data?.source === 'product-form-component';
    const count = comingFromProductForm ? this.#readCount() + nextCount : nextCount;

    this.#render(count);
  };

  /**
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    // bfcache restore: the server-rendered value is still correct.
  };

  #readCount() {
    const match = (this.textContent ?? '').match(/(\d+)/);
    return match ? parseInt(match[1] ?? '0', 10) : 0;
  }

  /**
   * @param {number} count
   */
  #render(count) {
    this.textContent = this.#template.replace('__COUNT__', String(count));
  }
}

if (!customElements.get('cart-count-text')) {
  customElements.define('cart-count-text', CartCountText);
}
